import notifier from '@albert-io/notifier';
import apolloClient from 'apollo/client';
import constants from 'client/constants';
import {notFound, redirect} from 'client/routeHelpers';

import {SubjectViewDocument} from './SubjectView.queries.generated';

type RouterState = {
  params: {slug: string};
  location: {pathname: string};
};

/**
 * Route `onEnter` hook for the subject-page route.
 * Fetches the subject via GQL to determine guide types and redirects to the
 * correct tab (free-response or assessments) when practice guides are absent.
 * Also primes the Apollo cache so SubjectView gets an instant cache hit.
 *
 * `subjectFound` lets the route tell "no subject has this slug" (a 404 for any
 * URL shaped like `/:slug`) apart from "the lookup failed", which is not a 404.
 */
export async function subjectPageOnEnter(
  nextState: RouterState,
  replace: (path: string) => void,
  callback: () => void
): Promise<{subjectFound: boolean}> {
  const {slug} = nextState.params;
  let redirectPath: string | undefined;
  let subjectFound = true;
  const routes = {
    assessment: `/${slug}/assessments`,
    free_response: `/${slug}/free-response`
  };

  try {
    const {data} = await apolloClient.query({
      query: SubjectViewDocument,
      variables: {slug}
    });
    const subject = data?.subjects?.results?.[0];
    subjectFound = Boolean(subject);
    const guideTypes = subject?.guides?.map((g: {type: string}) => g.type) ?? [];

    if (
      !nextState.location.pathname.includes(`/${slug}/overview`) &&
      !guideTypes.includes(constants.GUIDE_TYPES.PRACTICE)
    ) {
      if (guideTypes.includes(constants.GUIDE_TYPES.FREE_RESPONSE)) {
        redirectPath = routes.free_response;
      } else if (guideTypes.includes(constants.GUIDE_TYPES.ASSESSMENT)) {
        redirectPath = routes.assessment;
      }
    }
  } catch (err) {
    // A failed lookup is not a missing subject; let the view handle it.
    notifier.notify(err, {severity: 'error'});
  }

  const shouldRedirect =
    !!redirectPath &&
    !nextState.location.pathname.includes(redirectPath) &&
    !nextState.location.pathname.includes(`/${slug}/overview`);

  if (shouldRedirect) {
    replace(redirectPath!);
  }
  callback();
  return {subjectFound};
}

/**
 * `beforeLoad` for the `/:slug` subject route.
 *
 * Lives here rather than in the route tree so the not-found behavior is
 * testable without pulling in every route.
 */
export async function subjectPageBeforeLoad({params, location}: RouterState): Promise<void> {
  let redirectTo: string | undefined;
  const {subjectFound} = await subjectPageOnEnter(
    {params, location},
    (path: string) => {
      redirectTo = path;
    },
    () => undefined
  );
  if (redirectTo) {
    throw redirect({to: redirectTo} as never);
  }
  // `/:slug` matches any single-segment URL, so an unknown slug is an unknown
  // page: hand it to the root notFoundComponent instead of rendering a subject.
  if (!subjectFound) {
    throw notFound();
  }
}
